import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AllInclusiveOutlinedIcon from "@mui/icons-material/AllInclusiveOutlined";
import PersonPinCircleOutlinedIcon from "@mui/icons-material/PersonPinCircleOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Skeleton,
  TextField,
} from "@mui/material";

import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { OptionItem } from "components/atoms/SearchSelect/SearchSelect";
import { useAppFeedback } from "hooks/useAppFeedback";
import stylesButton from "styles/Button.module.css";
import stylesGrid from "styles/Grid.module.css";
import stylesInput from "styles/Input.module.css";
import stylesSurface from "styles/Surface.module.css";
import stylesGeneral from "styles/General.module.css";
import { convertListSelect } from "utils/Arrays";

import styles from "./GestaoVagasTurma.module.css";
import { useGestaoVagasTurma } from "./hooks/useGestaoVagasTurma";

interface GestaoVagasTurmaProps {
  onSaveSuccess?: () => void;
}

type CsvMunicipioItem = {
  codigo_ibge: number;
  limite_municipal: number;
  limite_estadual: number;
};

const metodoLabels = {
  REGIONAL: {
    title: "Polos pré-cadastrados",
    subtitle: "Adiciona os municípios vinculados aos polos escolhidos.",
    icon: <Groups2OutlinedIcon />,
  },
  MANUAL: {
    title: "Seleção manual",
    subtitle: "Pesquise e inclua vários municípios por vez.",
    icon: <PersonPinCircleOutlinedIcon />,
  },
  CSV: {
    title: "Importar CSV",
    subtitle: "Carrega municípios em lote com base no arquivo CSV.",
    icon: <UploadFileOutlinedIcon />,
  },
  ESTADO: {
    title: "Todos os municípios",
    subtitle: "Adiciona toda a base de municípios do estado.",
    icon: <ApartmentOutlinedIcon />,
  },
} as const;

const distribuicaoLabels = {
  SEM_LIMITE: {
    title: "Sem limite definido",
    subtitle:
      "Não haverá limite individual de vagas por município ou por estado.",
    icon: <AllInclusiveOutlinedIcon />,
  },
  IGUAL: {
    title: "Mesmo limite para todos",
    subtitle:
      "Defina um limite padrão e, se necessário, permita exceções por município.",
    icon: <Groups2OutlinedIcon />,
  },
} as const;

const parseCsvLine = (line: string, delimiter: string) => {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === delimiter && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }
    current += char;
  }

  values.push(current);
  return values.map((item) => item.trim());
};

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const sortMunicipiosByNome = <T extends { nome: string }>(items: T[]) =>
  [...items].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

const GestaoVagasTurma = ({ onSaveSuccess }: GestaoVagasTurmaProps) => {
  const [searchParams] = useSearchParams();
  const turmaId = searchParams.get("id");
  const { showError, showSuccess } = useAppFeedback();

  const {
    msgError,
    msgSuccess,
    regionais,
    municipios,
    loadingConfigVagas,
    loadingRegionais,
    loadingMunicipios,
    loadingSave,
    metodoSelecao,
    setMetodoSelecao,
    selectedRegional,
    setSelectedRegional,
    selectedMunicipiosManual,
    setSelectedMunicipiosManual,
    selectedMunicipioItems,
    criterio,
    setCriterio,
    semLimiteVagas,
    setSemLimiteVagas,
    limiteTotal,
    setLimiteTotal,
    limiteMunicipal,
    setLimiteMunicipal,
    limiteEstadual,
    setLimiteEstadual,
    modoDistribuicao,
    setModoDistribuicao,
    limitePorMunicipio,
    setLimitePorMunicipio,
    limitePorEstado,
    setLimitePorEstado,
    permitirExcecoes,
    togglePermitirExcecoes,
    permiteLimiteMunicipal,
    permiteLimiteEstadual,
    publicoTurmaCarregado,
    somaAmbitos,
    excedeLimiteTotal,
    mediaVagasPorMunicipio,
    addMunicipiosManual,
    addMunicipiosByRegionais,
    addTodosMunicipiosEstado,
    addMunicipiosFromCsvImport,
    removeMunicipio,
    clearMunicipios,
    updateMunicipioNumVagas,
    saveConfigVagas,
  } = useGestaoVagasTurma(turmaId);

  const [selectedSearch, setSelectedSearch] = useState("");
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvNotFound, setCsvNotFound] = useState<string[]>([]);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [addingMunicipiosMethod, setAddingMunicipiosMethod] = useState<
    "REGIONAL" | "MANUAL" | "ESTADO" | null
  >(null);
  const [exceptionSearch, setExceptionSearch] = useState("");
  const [exceptionPage, setExceptionPage] = useState(1);
  const [limiteTotalInput, setLimiteTotalInput] = useState(
    limiteTotal > 0 ? String(limiteTotal) : "",
  );

  useEffect(() => {
    if (msgSuccess) showSuccess(msgSuccess);
  }, [msgSuccess, showSuccess]);

  useEffect(() => {
    if (msgError) showError(msgError);
  }, [msgError, showError]);

  useEffect(() => {
    setLimiteTotalInput(limiteTotal > 0 ? String(limiteTotal) : "");
  }, [limiteTotal]);

  const municipioOptions = useMemo<OptionItem[]>(
    () => municipios.map((item) => ({ id: item.id, nome: item.nome })),
    [municipios],
  );

  const selectedMunicipioItemsSorted = useMemo(
    () => sortMunicipiosByNome(selectedMunicipioItems),
    [selectedMunicipioItems],
  );

  const municipiosSelecionadosFiltrados = useMemo(() => {
    const termo = selectedSearch.trim().toLowerCase();
    if (!termo) return selectedMunicipioItemsSorted;
    return selectedMunicipioItemsSorted.filter((item) =>
      item.nome.toLowerCase().includes(termo),
    );
  }, [selectedMunicipioItemsSorted, selectedSearch]);

  const exceptionMunicipios = useMemo(() => {
    const term = exceptionSearch.trim().toLocaleLowerCase("pt-BR");
    if (!term) return selectedMunicipioItemsSorted;
    return selectedMunicipioItemsSorted.filter((item) =>
      item.nome.toLocaleLowerCase("pt-BR").includes(term),
    );
  }, [exceptionSearch, selectedMunicipioItemsSorted]);

  const exceptionMunicipioIds = useMemo(
    () => selectedMunicipioItems.map((item) => item.id).sort((first, second) => first - second).join(","),
    [selectedMunicipioItems],
  );

  const exceptionPerPage = 10;
  const exceptionTotalPages = Math.max(
    1,
    Math.ceil(exceptionMunicipios.length / exceptionPerPage),
  );
  const currentExceptionPage = Math.min(exceptionPage, exceptionTotalPages);
  const paginatedExceptionMunicipios = useMemo(() => {
    const start = (currentExceptionPage - 1) * exceptionPerPage;
    return exceptionMunicipios.slice(start, start + exceptionPerPage);
  }, [currentExceptionPage, exceptionMunicipios]);

  useEffect(() => {
    setExceptionPage(1);
  }, [exceptionSearch, exceptionMunicipioIds]);

  const exportMunicipiosSelecionados = () => {
    if (selectedMunicipioItems.length === 0) return;

    const headers = [
      "codigo_ibge",
      "nome",
      "sigla",
      "limite_municipal",
      "limite_estadual",
    ];
    const rows = selectedMunicipioItems.map((item) => [
      item.codigo_ibge,
      `"${item.nome.replace(/"/g, '""')}"`,
      item.sigla,
      item.limite_municipal,
      item.limite_estadual,
    ]);

    const content = [
      headers.join(";"),
      ...rows.map((row) => row.join(";")),
    ].join("\n");
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "municipios_participantes.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadModeloCsv = () => {
    if (municipios.length === 0) return;

    const headers = [
      "codigo_ibge",
      "nome",
      "sigla",
      "limite_municipal",
      "limite_estadual",
    ];
    const rows = sortMunicipiosByNome(municipios).map((municipio) => [
      municipio.codigo_ibge,
      `"${municipio.nome.replace(/"/g, '""')}"`,
      municipio.sigla,
      0,
      0,
    ]);

    const content = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo_municipios_mg.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleLimiteTotalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, "");

    if (rawValue === "") {
      setLimiteTotalInput("");
      setLimiteTotal(0);
      return;
    }

    const normalizedValue = rawValue.replace(/^0+(?=\d)/, "");
    setLimiteTotalInput(normalizedValue);
    setLimiteTotal(Number(normalizedValue) || 0);
  };

  const handleCsvImport = async (file: File) => {
    setLoadingCsv(true);
    setCsvError(null);
    setCsvNotFound([]);

    try {
      const fileContent = await file.text();
      const lines = fileContent
        .split(/\r?\n/)
        .filter((line) => line.trim().length > 0);
      if (lines.length <= 1) {
        setCsvError("Arquivo CSV vazio ou sem linhas de dados.");
        return;
      }

      const delimiter = lines[0].includes(";") ? ";" : ",";
      const header = parseCsvLine(lines[0], delimiter).map((item) =>
        item.toLowerCase(),
      );
      const codigoIbgeIndex = header.indexOf("codigo_ibge");
      const nomeIndex = header.indexOf("nome");
      const limiteMunicipalIndex = header.indexOf("limite_municipal");
      const limiteEstadualIndex = header.indexOf("limite_estadual");

      if (codigoIbgeIndex === -1 && nomeIndex === -1) {
        setCsvError(
          "CSV invalido: informe ao menos a coluna codigo_ibge ou nome.",
        );
        return;
      }

      const municipioByCodigo = new Map<
        number,
        OptionItem & { codigo_ibge: number }
      >();
      const municipioByNome = new Map<
        string,
        OptionItem & { codigo_ibge: number }
      >();
      municipios.forEach((municipio) => {
        municipioByCodigo.set(municipio.codigo_ibge, municipio);
        const nomeNormalizado = normalizeText(municipio.nome);
        if (!municipioByNome.has(nomeNormalizado)) {
          municipioByNome.set(nomeNormalizado, municipio);
        }
      });

      const importedMap = new Map<number, CsvMunicipioItem>();
      const notFound: string[] = [];

      lines.slice(1).forEach((line, index) => {
        const values = parseCsvLine(line, delimiter);
        const linhaCsv = index + 2;
        const codigoIbgeValue =
          codigoIbgeIndex >= 0 ? (values[codigoIbgeIndex] ?? "") : "";
        const nomeValue = nomeIndex >= 0 ? (values[nomeIndex] ?? "") : "";
        const codigoIbgeNumerico = Number.parseInt(codigoIbgeValue, 10);

        const municipio =
          (Number.isNaN(codigoIbgeNumerico)
            ? undefined
            : municipioByCodigo.get(codigoIbgeNumerico)) ||
          (nomeValue
            ? municipioByNome.get(normalizeText(nomeValue))
            : undefined);

        if (!municipio) {
          notFound.push(
            `Linha ${linhaCsv} - ${codigoIbgeValue || nomeValue || "municipio invalido"}`,
          );
          return;
        }

        importedMap.set(municipio.codigo_ibge, {
          codigo_ibge: municipio.codigo_ibge,
          limite_municipal:
            Number.parseInt(values[limiteMunicipalIndex] ?? "0", 10) || 0,
          limite_estadual:
            Number.parseInt(values[limiteEstadualIndex] ?? "0", 10) || 0,
        });
      });

      const imported = Array.from(importedMap.values());
      if (imported.length === 0) {
        setCsvError("Nenhum município válido encontrado no CSV.");
        setCsvNotFound(notFound);
        return;
      }

      addMunicipiosFromCsvImport(imported);
      setCsvNotFound(notFound);
      if (notFound.length > 0) {
        setCsvError(
          "Alguns municípios não foram encontrados e foram ignorados.",
        );
      }
    } finally {
      setLoadingCsv(false);
    }
  };

  const handleAddMunicipios = (
    method: "REGIONAL" | "MANUAL" | "ESTADO",
    action: () => boolean,
  ) => {
    if (addingMunicipiosMethod) return;

    setAddingMunicipiosMethod(method);
    window.setTimeout(() => {
      action();
      window.setTimeout(() => setAddingMunicipiosMethod(null), 100);
    }, 0);
  };

  const handleTogglePermitirExcecoes = (checked: boolean) => {
    togglePermitirExcecoes(checked);
    setExceptionPage(1);
    if (!checked) {
      setExceptionSearch("");
    }
  };

  const renderMethodControls = () => {
    if (loadingMunicipios || loadingRegionais) {
      return (
        <div className={styles.methodControls}>
          <CircularProgress />
        </div>
      );
    }

    switch (metodoSelecao) {
      case "REGIONAL":
        return (
          <div className={styles.methodControls}>
            <MultiSelectCheck
              label=""
              placeholder="Selecione um ou mais polos"
              fullWidth
              value={selectedRegional}
              onChange={(items) =>
                setSelectedRegional(items.map((item) => Number(item)))
              }
              options={convertListSelect(regionais, "id", "nome")}
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={stylesInput.titleInputSoft}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
            <div className={styles.methodActions}>
              <Button
                variant="contained"
                className={stylesButton.buttonSoftPrimary}
                onClick={() =>
                  handleAddMunicipios("REGIONAL", addMunicipiosByRegionais)
                }
                disabled={addingMunicipiosMethod !== null}
                startIcon={
                  addingMunicipiosMethod === "REGIONAL" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddOutlinedIcon />
                  )
                }
              >
                {addingMunicipiosMethod === "REGIONAL"
                  ? "Adicionando..."
                  : "Adicionar municípios"}
              </Button>
            </div>
          </div>
        );

      case "MANUAL":
        return (
          <div className={styles.methodControls}>
            <MultiSelectCheck
              label=""
              placeholder="Selecione um ou mais municípios"
              fullWidth
              value={selectedMunicipiosManual}
              onChange={(items) =>
                setSelectedMunicipiosManual(items.map((item) => Number(item)))
              }
              options={municipioOptions}
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              titleInputClassname={stylesInput.titleInputSoft}
              inputSelectClassname={stylesInput.inputSoft}
              labelSelectClassname={stylesInput.labelSelect}
            />
            <div className={styles.methodActions}>
              <Button
                variant="contained"
                className={stylesButton.buttonSoftPrimary}
                startIcon={
                  addingMunicipiosMethod === "MANUAL" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddOutlinedIcon />
                  )
                }
                onClick={() =>
                  handleAddMunicipios("MANUAL", addMunicipiosManual)
                }
                disabled={addingMunicipiosMethod !== null}
              >
                {addingMunicipiosMethod === "MANUAL"
                  ? "Adicionando..."
                  : "Adicionar municípios"}
              </Button>
            </div>
          </div>
        );

      case "CSV":
        return (
          <div className={styles.methodControls}>
            <div className={styles.methodActions}>
              <input
                id="gestao-vagas-csv-input"
                type="file"
                accept=".csv"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) handleCsvImport(file);
                }}
              />
              <Button
                variant="contained"
                className={stylesButton.buttonSoftPrimary}
                startIcon={
                  loadingCsv ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <UploadFileOutlinedIcon />
                  )
                }
                onClick={() =>
                  document.getElementById("gestao-vagas-csv-input")?.click()
                }
                disabled={loadingCsv}
              >
                {loadingCsv ? "Importando..." : "Importar CSV"}
              </Button>
              <Button
                variant="outlined"
                className={stylesButton.buttonSoftOutline}
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={downloadModeloCsv}
                disabled={municipios.length === 0}
              >
                Baixar modelo
              </Button>
            </div>
            <div className={styles.methodActions}>
              <span className={styles.sectionDescription}>
                Use colunas `codigo_ibge`, `nome`, `limite_municipal` e
                `limite_estadual`.
              </span>
            </div>
          </div>
        );

      case "ESTADO":
        return (
          <div className={styles.methodControls}>
            <div className={styles.methodActions}>
              <span className={styles.sectionDescription}>
                Todos os municípios de MG serão adicionados. Registros já
                existentes serão ignorados.
              </span>
            </div>
            <div className={styles.methodActions}>
              <Button
                variant="contained"
                className={stylesButton.buttonSoftPrimary}
                onClick={() =>
                  handleAddMunicipios("ESTADO", addTodosMunicipiosEstado)
                }
                disabled={addingMunicipiosMethod !== null}
                startIcon={
                  addingMunicipiosMethod === "ESTADO" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddOutlinedIcon />
                  )
                }
              >
                {addingMunicipiosMethod === "ESTADO"
                  ? "Adicionando..."
                  : "Adicionar todos"}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loadingConfigVagas || loadingRegionais || loadingMunicipios) {
    return <GestaoVagasTurmaSkeleton />;
  }

  return (
    <>
      {publicoTurmaCarregado &&
        !permiteLimiteMunicipal &&
        !permiteLimiteEstadual && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Configure o público-alvo da turma para habilitar limites municipal e
            estadual.
          </Alert>
        )}

      <div>
        <div>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>1</div>
            <div>
              <div className={styles.sectionTitle}>
                Municipios participantes
              </div>
              <p className={styles.sectionDescription}>
                Escolha a forma de inclusão inicial. Municípios já adicionados
                serão ignorados para evitar duplicidade.
              </p>
            </div>
          </div>

          <div className={styles.methodGrid}>
            {(
              Object.keys(metodoLabels) as Array<keyof typeof metodoLabels>
            ).map((key) => {
              const method = metodoLabels[key];
              const active = metodoSelecao === key;

              return (
                <button
                  type="button"
                  key={key}
                  className={`${styles.methodCard} ${stylesSurface.selectableCard} ${active ? `${styles.methodCardActive} ${stylesSurface.selectableCardActive}` : ""}`}
                  onClick={() => setMetodoSelecao(key)}
                >
                  <div className={styles.choiceCardHeader}>
                    {method.icon}
                    <div>
                      <div className={styles.methodCardTitle}>
                        {method.title}
                      </div>
                      <div className={styles.methodCardSubtitle}>
                        {method.subtitle}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {renderMethodControls()}

          {csvError && (
            <div className={`${styles.infoBox} ${stylesSurface.infoBanner}`}>
              <InfoOutlinedIcon />
              <div>{csvError}</div>
            </div>
          )}

          {csvNotFound.length > 0 && (
            <div className={`${styles.infoBox} ${stylesSurface.infoBanner}`}>
              <InfoOutlinedIcon />
              <div>
                {csvNotFound.slice(0, 5).map((item) => (
                  <div key={item}>{item}</div>
                ))}
                {csvNotFound.length > 5 && (
                  <div>+ {csvNotFound.length - 5} linhas não exibidas</div>
                )}
              </div>
            </div>
          )}

          <div className={styles.selectedPanel}>
            <div className={styles.selectedPanelHeader}>
              <div className={styles.selectedPanelTitle}>
                Municípios selecionados ({selectedMunicipioItems.length})
              </div>

              <div className={styles.selectedPanelTools}>
                <div className={styles.selectedSearch}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    placeholder="Pesquisar municipio..."
                    value={selectedSearch}
                    onChange={(event) => setSelectedSearch(event.target.value)}
                    InputProps={{
                      startAdornment: <SearchOutlinedIcon fontSize="small" />,
                      className: stylesInput.inputSearchSoft,
                      disableUnderline: true,
                    }}
                  />
                </div>

                <Button
                  variant="outlined"
                  className={stylesButton.buttonSoftOutline}
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={exportMunicipiosSelecionados}
                  disabled={selectedMunicipioItems.length === 0}
                >
                  Exportar lista
                </Button>
              </div>
            </div>

            <div className={styles.selectedBody}>
              {municipiosSelecionadosFiltrados.length === 0 ? (
                <div className={styles.emptyState}>
                  Nenhum município selecionado.
                </div>
              ) : (
                <div className={styles.municipioGrid}>
                  {municipiosSelecionadosFiltrados.map((municipio) => (
                    <div
                      key={municipio.id}
                      className={`${styles.municipioTag} ${stylesSurface.tagSoft}`}
                    >
                      <span className={styles.municipioTagName}>
                        {municipio.nome}
                      </span>
                      <IconButton
                        size="small"
                        className={styles.iconButton}
                        onClick={() => removeMunicipio(municipio.id)}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.selectedFooter}>
              <Button
                variant="outlined"
                className={stylesButton.buttonSoftMuted}
                startIcon={<DeleteOutlineOutlinedIcon />}
                onClick={clearMunicipios}
                disabled={selectedMunicipioItems.length === 0}
              >
                Limpar seleção
              </Button>

              <div className={styles.sectionDescription}>
                {selectedMunicipioItems.length} municípios selecionados
              </div>
            </div>
          </div>

          <div className={`${styles.infoBox} ${stylesSurface.infoBanner}`}>
            <InfoOutlinedIcon />
            <div>
              Os municípios selecionados poderão ser ajustados a qualquer
              momento antes da abertura das inscrições.
            </div>
          </div>
        </div>

        <div className={stylesGeneral.mt6}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>2</div>
            <div>
              <div className={styles.sectionTitle}>
                Quantidade total de vagas da turma
              </div>
            </div>
          </div>

          <div className={styles.quantityGrid}>
            <div className={styles.quantityInputGroup}>
              <label className={styles.unlimitedVacanciesToggle}>
                <Checkbox
                  checked={semLimiteVagas}
                  onChange={(event) => setSemLimiteVagas(event.target.checked)}
                  inputProps={{ name: "sem_limite_vagas" }}
                />
                Sem limite de vagas
              </label>

              <TextField
                type="number"
                size="small"
                variant="standard"
                fullWidth
                value={limiteTotalInput}
                onChange={handleLimiteTotalChange}
                disabled={semLimiteVagas}
                InputProps={{
                  className: stylesInput.inputSoft,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={styles.summaryHighlight}>
              <span className={styles.summaryHighlightStrong}>
                {semLimiteVagas
                  ? "Vagas sem limite"
                  : `${limiteTotal} vagas disponíveis`}
              </span>
              <div>
                {selectedMunicipioItems.length} municípios participantes
                {!semLimiteVagas &&
                  ` - média de ${mediaVagasPorMunicipio} vagas por município`}
              </div>
            </div>
          </div>
        </div>

        <div className={stylesGeneral.mt6}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>3</div>
            <div>
              <div className={styles.sectionTitle}>
                Critérios de ocupação do total de vagas da turma
              </div>
            </div>
          </div>

          <div
            className={`${styles.occupationGrid} ${semLimiteVagas ? styles.disabledConfiguration : ""}`}
          >
            <button
              type="button"
              className={`${styles.choiceCard} ${stylesSurface.selectableCard} ${criterio === "LIVRE" ? `${styles.choiceCardActive} ${stylesSurface.selectableCardActive}` : ""}`}
              disabled={semLimiteVagas}
              onClick={() => {
                setCriterio("LIVRE");
                setLimiteMunicipal(0);
                setLimiteEstadual(0);
              }}
            >
              <div className={styles.choiceCardHeader}>
                <Checkbox
                  checked={criterio === "LIVRE"}
                  disabled={semLimiteVagas}
                />
                <div>
                  <div className={styles.choiceCardTitle}>Ocupacao livre</div>
                  <div className={styles.choiceCardText}>
                    Todas as vagas ficam disponíveis para qualquer público.
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              className={`${styles.choiceCard} ${stylesSurface.selectableCard} ${criterio === "MISTO" ? `${styles.choiceCardActive} ${stylesSurface.selectableCardActive}` : ""}`}
              onClick={() => setCriterio("MISTO")}
              disabled={semLimiteVagas}
            >
              <div className={styles.choiceCardHeader}>
                <Checkbox
                  checked={criterio === "MISTO"}
                  disabled={semLimiteVagas}
                />
                <div>
                  <div className={styles.choiceCardTitle}>
                    Ocupação proporcional
                  </div>
                  <div className={styles.choiceCardText}>
                    Defina a quantidade de vagas por público sem ultrapassar o
                    total disponível.
                  </div>
                </div>
              </div>

              {criterio === "MISTO" && (
                <div className={styles.occupationInputs}>
                  <TextField
                    type="number"
                    size="small"
                    label="Público municipal"
                    variant="standard"
                    value={limiteMunicipal}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setLimiteMunicipal(Number(event.target.value) || 0)
                    }
                    disabled={semLimiteVagas || !permiteLimiteMunicipal}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      className: stylesInput.inputSoftCompact,
                      disableUnderline: true,
                    }}
                  />

                  <TextField
                    type="number"
                    size="small"
                    label="Público estadual"
                    variant="standard"
                    value={limiteEstadual}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setLimiteEstadual(Number(event.target.value) || 0)
                    }
                    disabled={semLimiteVagas || !permiteLimiteEstadual}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      className: stylesInput.inputSoftCompact,
                      disableUnderline: true,
                    }}
                  />

                  <div className={styles.summaryHighlight}>
                    <span className={styles.summaryHighlightStrong}>
                      Total {somaAmbitos}
                    </span>
                    <div>
                      {excedeLimiteTotal
                        ? "Soma ultrapassa o total"
                        : "Soma confere"}
                    </div>
                  </div>
                </div>
              )}
            </button>
          </div>

          {!semLimiteVagas && excedeLimiteTotal && (
            <div className={`${styles.infoBox} ${stylesSurface.infoBanner}`}>
              <InfoOutlinedIcon />
              <div>
                A soma das vagas municipal e estadual não pode ultrapassar o
                total da turma.
              </div>
            </div>
          )}
        </div>

        <div className={stylesGeneral.mt6}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>4</div>
            <div>
              <div className={styles.sectionTitle}>
                Distribuição de vagas por município
              </div>
            </div>
          </div>

          <div
            className={`${styles.distributionTop} ${semLimiteVagas ? styles.disabledConfiguration : ""}`}
          >
            {(
              Object.keys(distribuicaoLabels) as Array<
                keyof typeof distribuicaoLabels
              >
            ).map((key) => {
              const item = distribuicaoLabels[key];
              const active = modoDistribuicao === key;

              return (
                <button
                  type="button"
                  key={key}
                  className={`${styles.choiceCard} ${stylesSurface.selectableCard} ${active ? `${styles.choiceCardActive} ${stylesSurface.selectableCardActive}` : ""}`}
                  onClick={() => setModoDistribuicao(key)}
                  disabled={semLimiteVagas}
                >
                  <div className={styles.choiceCardHeader}>
                    {item.icon}
                    <div>
                      <div className={styles.choiceCardTitle}>{item.title}</div>
                      <div className={styles.choiceCardText}>
                        {item.subtitle}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {modoDistribuicao === "IGUAL" && (
            <div className={semLimiteVagas ? styles.disabledConfiguration : ""}>
              <div className={styles.distributionInputs}>
                <TextField
                  type="number"
                  size="small"
                  label="Limite municipal"
                  variant="standard"
                  value={limitePorMunicipio}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setLimitePorMunicipio(
                      Math.max(0, Number(event.target.value) || 0),
                    )
                  }
                  disabled={semLimiteVagas || !permiteLimiteMunicipal}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    className: stylesInput.inputSoftCompact,
                    disableUnderline: true,
                  }}
                />

                <TextField
                  type="number"
                  size="small"
                  label="Limite estadual"
                  variant="standard"
                  value={limitePorEstado}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setLimitePorEstado(
                      Math.max(0, Number(event.target.value) || 0),
                    )
                  }
                  disabled={semLimiteVagas || !permiteLimiteEstadual}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    className: stylesInput.inputSoftCompact,
                    disableUnderline: true,
                  }}
                />
              </div>

              <div className={styles.exceptionHeader}>
                <div>
                  <div className={styles.choiceCardTitle}>
                    Exceções por município
                  </div>
                  <div className={styles.choiceCardText}>
                    Ao habilitar, os limites globais atuais serão aplicados em
                    toda a lista antes da edição individual.
                  </div>
                </div>

                <label className={styles.choiceCardText}>
                  <Checkbox
                    checked={permitirExcecoes}
                    onChange={(event) =>
                      handleTogglePermitirExcecoes(event.target.checked)
                    }
                    disabled={semLimiteVagas}
                  />
                  Permitir exceções por município
                </label>
              </div>

              <div className={styles.tableWrap}>
                {!permitirExcecoes && (
                  <div
                    className={`${styles.infoBox} ${stylesSurface.infoBanner}`}
                  >
                    <InfoOutlinedIcon />
                    <div>
                      A lista abaixo mostra os municipios selecionados. Habilite
                      excecoes por municipio para editar os limites
                      individualmente.
                    </div>
                  </div>
                )}

                {permitirExcecoes && (
                  <div className={styles.exceptionListTools}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      placeholder="Pesquisar município..."
                      value={exceptionSearch}
                      onChange={(event) =>
                        setExceptionSearch(event.target.value)
                      }
                      InputProps={{
                        startAdornment: <SearchOutlinedIcon fontSize="small" />,
                        className: stylesInput.inputSearchSoft,
                        disableUnderline: true,
                      }}
                    />
                  </div>
                )}

                <div className={styles.tableHeader}>
                  <div>Município</div>
                  <div>Limite municipal</div>
                  <div>Limite estadual</div>
                  <div>Ações</div>
                </div>

                {exceptionMunicipios.length === 0 ? (
                  <div className={styles.emptyState}>
                    {permitirExcecoes
                      ? "Nenhum município encontrado para a pesquisa."
                      : "Nenhum município selecionado."}
                  </div>
                ) : (
                  paginatedExceptionMunicipios.map((municipio) => (
                    <div key={municipio.id} className={styles.tableRow}>
                      <div className={styles.tableRowName}>
                        {municipio.nome}
                      </div>

                      <div className={styles.tableInput}>
                        <TextField
                          type="number"
                          size="small"
                          variant="standard"
                          fullWidth
                          value={municipio.limite_municipal}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            updateMunicipioNumVagas(
                              municipio.id,
                              "limite_municipal",
                              Number(event.target.value) || 0,
                            )
                          }
                          disabled={
                            semLimiteVagas ||
                            !permiteLimiteMunicipal ||
                            !permitirExcecoes
                          }
                          InputProps={{
                            className: stylesInput.inputSoftCompact,
                            disableUnderline: true,
                          }}
                        />
                      </div>

                      <div className={styles.tableInput}>
                        <TextField
                          type="number"
                          size="small"
                          variant="standard"
                          fullWidth
                          value={municipio.limite_estadual}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            updateMunicipioNumVagas(
                              municipio.id,
                              "limite_estadual",
                              Number(event.target.value) || 0,
                            )
                          }
                          disabled={
                            semLimiteVagas ||
                            !permiteLimiteEstadual ||
                            !permitirExcecoes
                          }
                          InputProps={{
                            className: stylesInput.inputSoftCompact,
                            disableUnderline: true,
                          }}
                        />
                      </div>

                      <div>
                        <Button
                          variant="text"
                          className={stylesButton.buttonInlineLink}
                          onClick={() => removeMunicipio(municipio.id)}
                          disabled={semLimiteVagas}
                        >
                          Remover
                        </Button>
                      </div>
                    </div>
                  ))
                )}

                {exceptionMunicipios.length > 0 && (
                  <div className={styles.exceptionPagination}>
                    <span>
                      Exibindo{" "}
                      {(currentExceptionPage - 1) * exceptionPerPage + 1} a{" "}
                      {Math.min(
                        currentExceptionPage * exceptionPerPage,
                        exceptionMunicipios.length,
                      )}{" "}
                      de {exceptionMunicipios.length}
                    </span>
                    <div>
                      <IconButton
                        size="small"
                        aria-label="Página anterior"
                        disabled={currentExceptionPage === 1}
                        onClick={() =>
                          setExceptionPage((current) =>
                            Math.max(1, current - 1),
                          )
                        }
                      >
                        <KeyboardArrowLeftOutlinedIcon fontSize="small" />
                      </IconButton>
                      <span>
                        Página {currentExceptionPage} de {exceptionTotalPages}
                      </span>
                      <IconButton
                        size="small"
                        aria-label="Próxima página"
                        disabled={currentExceptionPage === exceptionTotalPages}
                        onClick={() =>
                          setExceptionPage((current) =>
                            Math.min(exceptionTotalPages, current + 1),
                          )
                        }
                      >
                        <KeyboardArrowRightOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <section className={styles.configSummary}>
          <div className={styles.configSummaryTitle}>
            Resumo da configuração
          </div>
          <div className={styles.configSummaryGrid}>
            <div className={styles.configSummaryItem}>
              <div className={styles.configSummaryHeader}>
                <GroupOutlinedIcon className={styles.configSummaryIcon} />
                <div className={styles.configSummaryLabel}>
                  Municípios participantes
                </div>
              </div>
              <div className={styles.configSummaryValue}>
                {selectedMunicipioItems.length} municípios
              </div>
            </div>
            <div className={styles.configSummaryItem}>
              <div className={styles.configSummaryHeader}>
                <FactCheckOutlinedIcon className={styles.configSummaryIcon} />
                <div className={styles.configSummaryLabel}>Total de vagas</div>
              </div>
              <div className={styles.configSummaryValue}>
                {semLimiteVagas ? "Sem limite" : `${limiteTotal} vagas`}
              </div>
            </div>
            <div className={styles.configSummaryItem}>
              <div className={styles.configSummaryHeader}>
                <Groups2OutlinedIcon className={styles.configSummaryIcon} />
                <div className={styles.configSummaryLabel}>
                  Critério de ocupação
                </div>
              </div>
              <div className={styles.configSummaryValue}>
                {semLimiteVagas
                  ? "Sem limite de vagas"
                  : criterio === "MISTO"
                    ? "Proporcional"
                    : "Livre"}
              </div>
            </div>
            <div className={styles.configSummaryItem}>
              <div className={styles.configSummaryHeader}>
                <ApartmentOutlinedIcon className={styles.configSummaryIcon} />
                <div className={styles.configSummaryLabel}>
                  Vagas municipais
                </div>
              </div>
              <div className={styles.configSummaryValue}>
                {semLimiteVagas
                  ? "Sem limite"
                  : `${permiteLimiteMunicipal ? limiteMunicipal : 0} vagas`}
              </div>
            </div>
            <div className={styles.configSummaryItem}>
              <div className={styles.configSummaryHeader}>
                <AccountBalanceOutlinedIcon
                  className={styles.configSummaryIcon}
                />
                <div className={styles.configSummaryLabel}>Vagas estaduais</div>
              </div>
              <div className={styles.configSummaryValue}>
                {semLimiteVagas
                  ? "Sem limite"
                  : `${permiteLimiteEstadual ? limiteEstadual : 0} vagas`}
              </div>
            </div>
            <div className={styles.configSummaryItem}>
              <div className={styles.configSummaryHeader}>
                <TuneOutlinedIcon className={styles.configSummaryIcon} />
                <div className={styles.configSummaryLabel}>Distribuição</div>
              </div>
              <div className={styles.configSummaryValue}>
                {semLimiteVagas || modoDistribuicao === "SEM_LIMITE"
                  ? "Sem limite definido"
                  : permitirExcecoes
                    ? "Limite padrao com excecoes"
                    : "Mesmo limite para todos"}
              </div>
            </div>
          </div>
        </section>

        <div className={`${stylesGrid.inputGroupFull} ${styles.actionsRow}`}>
          <Button
            variant="contained"
            onClick={async () => {
              const success = await saveConfigVagas();
              if (success) onSaveSuccess?.();
            }}
            className={`${stylesButton.buttonSoftPrimary} ${loadingSave ? stylesButton.buttonLoad : ""}`}
            color="primary"
            disabled={loadingSave}
          >
            {loadingSave ? (
              <>
                <div className={stylesButton.spinner}></div>
                <span>Salvando...</span>
              </>
            ) : (
              <span>Salvar configuração</span>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

const GestaoVagasTurmaSkeleton = () => (
  <div
    className={styles.skeletonContainer}
    aria-busy="true"
    aria-label="Carregando gestão de vagas"
  >
    <section className={styles.sectionCard}>
      <div className={styles.skeletonHeader}>
        <Skeleton variant="rounded" width={32} height={32} />
        <div>
          <Skeleton width={230} height={26} />
          <Skeleton width={510} height={20} />
        </div>
      </div>
      <div className={styles.methodGrid}>
        {[0, 1, 2, 3].map((item) => (
          <Skeleton key={item} variant="rounded" height={120} />
        ))}
      </div>
      <Skeleton
        className={styles.skeletonSpacing}
        variant="rounded"
        height={44}
      />
      <Skeleton variant="rounded" height={180} />
    </section>
    <section className={styles.sectionCard}>
      <div className={styles.skeletonHeader}>
        <Skeleton variant="rounded" width={32} height={32} />
        <div>
          <Skeleton width={270} height={26} />
          <Skeleton width={430} height={20} />
        </div>
      </div>
      <Skeleton variant="rounded" height={90} />
    </section>
    <section className={styles.sectionCard}>
      <div className={styles.skeletonHeader}>
        <Skeleton variant="rounded" width={32} height={32} />
        <Skeleton width={300} height={26} />
      </div>
      <div className={styles.skeletonTwoColumns}>
        <Skeleton variant="rounded" height={150} />
        <Skeleton variant="rounded" height={150} />
      </div>
    </section>
    <section className={styles.sectionCard}>
      <div className={styles.skeletonHeader}>
        <Skeleton variant="rounded" width={32} height={32} />
        <Skeleton width={340} height={26} />
      </div>
      <Skeleton variant="rounded" height={175} />
    </section>
  </div>
);

export default GestaoVagasTurma;
